import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { useUnit } from '../contexts/UnitContext';
import { kpiApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Printer, QrCode, ShieldCheck } from 'lucide-react';

export default function KpiQrSheet() {
  const { user } = useAuth();
  const { activeUnit } = useUnit();
  const navigate = useNavigate();
  // Each employee has ONE permanent QR. The server derives the token from the
  // employee id, so this list always carries it and the sheet reprints forever.
  const [rows, setRows] = useState([]);
  const [dept, setDept] = useState('');
  const [loading, setLoading] = useState(true);

  const loadStatus = async () => {
    try {
      const r = await kpiApi.tokens({ unit: activeUnit });
      setRows(r.data || []);
    } catch (e) { toast.error('Could not load KPI links'); setRows([]); }
  };

  useEffect(() => {
    if (!(user?.role === 'admin' || (user?.permissions || []).includes('kpi.admin'))) { navigate('/', { replace: true }); return; }
    (async () => { setLoading(true); await loadStatus(); setLoading(false); })();
    // eslint-disable-next-line
  }, [user, activeUnit]);

  const depts = useMemo(() => [...new Set(rows.map((r) => r.department).filter(Boolean))].sort(), [rows]);
  const visible = useMemo(() => {
    const list = dept ? rows.filter((r) => r.department === dept) : rows;
    return [...list].sort((a, b) => (a.unit || '').localeCompare(b.unit || '') || (a.department || '').localeCompare(b.department || '') || (a.name || '').localeCompare(b.name || ''));
  }, [rows, dept]);

  const urlFor = (e) => `${window.location.origin}/kpi/${e.token}`;

  const copyOne = (e) => {
    if (!e.token) return;
    navigator.clipboard?.writeText(urlFor(e));
    toast.success(`Link copied for ${e.name}`);
  };

  return (
    <div data-testid="kpi-qr-sheet-page" className="flex h-screen app-shell">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #qr-print-area, #qr-print-area * { visibility: visible; }
          #qr-print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 0; }
          .qr-card { break-inside: avoid; page-break-inside: avoid; }
          @page { size: A4; margin: 10mm; }
        }
      `}</style>
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-auto p-5 sm:p-6 lg:p-8 page-enter">
          <div className="max-w-[1200px] mx-auto">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white" style={{ background: 'linear-gradient(135deg,#15307F,#2563EB)' }}>
                  <QrCode className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold tracking-tight text-[#11182B]">KPI QR sheets</h1>
                  <p className="text-sm text-[#5A6377]">{visible.length} employees{activeUnit ? ` · ${activeUnit}` : ''} · print, cut and distribute. Each QR opens that employee&apos;s personal form.</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select data-testid="qr-dept-filter" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2"
                        value={dept} onChange={(e) => setDept(e.target.value)}>
                  <option value="">All departments</option>
                  {depts.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <Button data-testid="qr-print-button" onClick={() => window.print()} disabled={loading || visible.length === 0}>
                  <Printer className="w-4 h-4 mr-1" /> Print visible cards
                </Button>
              </div>
            </div>

            <div className="mb-4 flex items-start gap-2 text-[12px] text-[#15654B] bg-[#EAF7F0] border border-[#CDEBDB] rounded-md p-3">
              <ShieldCheck className="w-4 h-4 mt-[1px] flex-shrink-0" />
              <div>
                Every employee has <b>one permanent QR code</b>, tied to their employee record for life. Reprint this sheet any time — the codes never change and previously distributed cards keep working.
              </div>
            </div>

            {loading ? <div className="text-sm text-[#5A6377]">Loading…</div>
              : visible.length === 0 ? <div className="bg-white border border-[#E6E9F0] rounded-xl p-8 text-center text-sm text-[#5A6377]">No employees in this scope.</div>
              : (
              <div id="qr-print-area" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {visible.map((e) => (
                  <div key={e.id} data-testid={`kpi-qr-card-${e.id}`} className="qr-card bg-white border border-[#D1D7E2] rounded-[10px] p-4 text-center">
                    <div className="text-[10px] font-bold tracking-widest text-[#15307F] uppercase mb-1">AAMPL · Employee Form</div>
                    <div className="flex justify-center my-2 min-h-[120px] items-center">
                      {e.token ? (
                        <QRCodeCanvas value={urlFor(e)} size={120} includeMargin level="M" />
                      ) : (
                        <div className="text-[10px] text-[#98A1B2] px-3">Link unavailable.</div>
                      )}
                    </div>
                    <div className="text-sm font-semibold text-[#11182B] leading-tight">{e.name}</div>
                    <div className="text-[11px] text-[#5A6377]">{e.card_no || '—'}</div>
                    <div className="text-[10px] text-[#98A1B2] uppercase tracking-wide">{e.department}{e.unit ? ` · ${e.unit}` : ''}</div>
                    <div className="text-[9px] text-[#98A1B2] mt-1">permanent code</div>
                    <div className="flex justify-center gap-1 mt-2 print:hidden">
                      <Button data-testid={`kpi-qr-copy-${e.id}`} size="sm" variant="outline" onClick={() => copyOne(e)}>Copy link</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
